import { useState, useEffect, useRef } from 'react';
import { isValidPhoneNumber } from 'react-phone-number-input';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import styled from 'styled-components';
import validator from 'validator';
import {
  Typography,
  Button,
  Input,
  InputPhoneNumber,
  enqueueSnackbar,
  SearchableSelect,
  BorderLine,
  HideInput,
  FileInput,
  FileRow,
  Select,
  InputNumber,
  BelongsToSelects,
} from '../../design-system';
import {
  DashboardHeader,
  DashboardForm,
  DashboardFormTwoColumn,
  DashboardFormCustomColumn,
} from '../dashboard';
import { getNotificationData } from '../notifications';
import { useCreateCompanyMutation, useUpdateClientDocumentsMutation } from '../../hooks';
import { getPhoneNumberValue, getNormalFormProps, getCompanyCode, showToRoles } from '../../utils';
import { belongsToOptions } from '../../services';
import {
  INPUT_ERRORS,
  REGEX,
  MESSAGES,
  countries,
  cities,
  messengerOptions,
  clientCategories,
  CountryOptions,
  StateOptions,
  gateOutAllowedOptions,
} from '../../const';
import { i18n } from '../../i18n';
import { Block, Messenger } from '../../types';
import { useStoreUser } from '../../context';

let formProps: any = {};

export const CompanyPage = styled.div`
  display: grid;
  grid-template-rows: max-content 1fr;
  grid-row-gap: 3rem;

  height: 100%;
`;

const getSubmitProps = (belongsTo: string) => {
  const {
    validators: { blocks, messengers, descriptions, ...validators },
  }: any = formProps;
  if (
    [
      !blocks.some((item: any) => !!item.block || !!item.rowLength || !!item.slotLength),
      !messengers.some((item: any) => !!item.label || !!item.identifier),
      ...descriptions.map((val: any) => !val),
      ...Object.values(validators).map((val) => !val),
    ].some((val) => !val)
  ) {
    return {};
  }

  formProps.phoneNumber = getPhoneNumberValue(formProps.phoneNumber);
  formProps.companyPhoneNumber = getPhoneNumberValue(formProps.companyPhoneNumber);
  delete formProps.isSubmitClicked;

  return {
    submitProps: { ...getNormalFormProps(formProps), belongsTo },
    resolveFn: formProps.resolveFn,
  };
};

export const CompanyHeader = ({ data = {}, belongsTo, showOnlySubmit }: any) => {
  const { mutate } = useCreateCompanyMutation();
  const updateDocumentsMutation = useUpdateClientDocumentsMutation();

  const handleSubmitClick = async () => {
    formProps.onSubmitClick();
    const { submitProps, resolveFn } = getSubmitProps(belongsTo);
    if (!submitProps) {
      return;
    }

    const uppdateDocumentsMutate = (id: string) => {
      if (belongsTo !== 'CLIENT') {
        resolveFn();
        return;
      }

      updateDocumentsMutation.mutate(
        {
          id,
          documents: submitProps.files.map((file: any) => ({
            action: 'upload',
            file: file,
            name: file.name,
          })),
          descriptions: submitProps.descriptions,
        },
        {
          onSettled: () => {
            resolveFn();
          },
        },
      );
    };

    mutate(submitProps, {
      onSuccess: (res) => {
        enqueueSnackbar(MESSAGES.BELONGS_TO.SUCCESS.CREATED, 'success');
        uppdateDocumentsMutate(res.data.id);
      },
      onError: (error: any) => {
        if (error.statusCode === 409) return;
        enqueueSnackbar(MESSAGES.BELONGS_TO.ERROR.NOT_CREATED, 'error');
      },
    });
  };

  let title = data.id
    ? `Edit Yard Company: ${data.companyLegalName}`
    : i18n.t('pages.yardCompaniesAdd.title');
  if (belongsTo === 'YARD')
    title = data.id ? `Edit Yard: ${data.companyLegalName}` : i18n.t('pages.yardsAdd.title');
  if (belongsTo === 'CLIENT')
    title = data.id ? `Edit Client: ${data.companyLegalName}` : i18n.t('pages.clientsAdd.title');

  return (
    <DashboardHeader>
      {!showOnlySubmit && (
        <>
          <Typography variant={'textXL'} weight={'bold'}>
            {title}
          </Typography>
          <Button size={'medium'} data-color={'white'} to={-1}>
            {i18n.t('button.goBack')}
          </Button>
        </>
      )}
      {showOnlySubmit && <span />}
      <Button size={'medium'} onClick={handleSubmitClick}>
        {i18n.t('button.submit')}
      </Button>
    </DashboardHeader>
  );
};

export const CompanyForm = ({ data = {}, belongsTo }: any) => {
  const { user } = useStoreUser();

  const [companyLegalName, setCompanyLegalName] = useState(data.companyLegalName || '');
  const [code, setCode] = useState(data.code || '');
  const [streetAddress, setStreetAddress] = useState(data.streetAddress || '');
  const [city, setCity] = useState(data.city || '');
  const [zipCode, setZipCode] = useState(data.zipCode || '');
  const [country, setCountry] = useState(data.country || '');
  const [contactPersonFirstName, setContactPersonFirstName] = useState(
    data.contactPersonFirstName || '',
  );
  const [contactPersonLastName, setContactPersonLastName] = useState(
    data.contactPersonLastName || '',
  );
  const [email, setEmail] = useState(data.email || '');
  const [phoneNumber, setPhoneNumber] = useState(data.phoneNumber ? data.phoneNumber : undefined);
  const [name, setName] = useState(data.name || '');
  const [companyEmail, setCompanyEmail] = useState(data.companyEmail || '');
  const [companyPhoneNumber, setCompanyPhoneNumber] = useState(
    data.companyPhoneNumber ? data.phoneNumber : undefined,
  );

  const [yardCompanyId, setYardCompanyId] = useState('');
  const [yardCompanyName, setYardCompanyName] = useState('');
  const [yardId, setYardId] = useState('');

  const [category, setCategory] = useState('NONE');
  const [gateOutAllowed, setGateOutAllowed] = useState(false);

  const [blocks, setBlocks] = useState<Block[]>([{ block: '', rowLength: '', slotLength: '' }]);
  const [messengers, setMessengers] = useState<Messenger[]>([{ label: '', identifier: '' }]);
  const [files, setFiles] = useState<any>([]);
  const [descriptions, setDescriptions] = useState<any>([]);
  const inputRef = useRef<any>();

  const [updateKey, setUpdate] = useState<any>(Math.random());

  const validators = {
    companyLegalName:
      belongsTo !== 'YARD'
        ? companyLegalName
          ? !validator.isLength(companyLegalName, { min: 4, max: 100 })
            ? INPUT_ERRORS.NOT_VALID.COMPANY_LEGAL_NAME_LENGTH
            : !REGEX.COMPANY_NAME.test(companyLegalName)
            ? INPUT_ERRORS.NOT_VALID.COMPANY_LEGAL_NAME
            : ''
          : true
        : '',
    code:
      belongsTo === 'CLIENT' || belongsTo === 'YARD'
        ? code
          ? !validator.isLength(code, { min: 4, max: 8 })
            ? INPUT_ERRORS.NOT_VALID.CODE_LENGTH
            : !REGEX.CODE_NEW.test(code)
            ? INPUT_ERRORS.NOT_VALID.CODE
            : ''
          : true
        : '',
    streetAddress: streetAddress
      ? !validator.isLength(streetAddress, { min: 5, max: 100 })
        ? INPUT_ERRORS.NOT_VALID.STREET_ADDRESS_LENGTH
        : !REGEX.STREET_ADDRESS.test(streetAddress)
        ? INPUT_ERRORS.NOT_VALID.STREET_ADDRESS
        : ''
      : true,
    city: city
      ? !validator.isLength(city, { min: 1, max: 85 })
        ? INPUT_ERRORS.NOT_VALID.CITY_LENGTH
        : !REGEX.CITY.test(city)
        ? INPUT_ERRORS.NOT_VALID.CITY
        : ''
      : true,
    zipCode: zipCode
      ? !validator.isLength(zipCode, { min: 2, max: 12 })
        ? INPUT_ERRORS.NOT_VALID.ZIP_CODE_LENGTH
        : !REGEX.ZIP_CODE.test(zipCode)
        ? INPUT_ERRORS.NOT_VALID.ZIP_CODE
        : ''
      : '',
    country: country
      ? !validator.isLength(country, { min: 1, max: 85 })
        ? INPUT_ERRORS.NOT_VALID.COUNTRY_LENGTH
        : !REGEX.COUNTRY.test(country)
        ? INPUT_ERRORS.NOT_VALID.COUNTRY
        : ''
      : true,
    contactPersonFirstName:
      belongsTo !== 'YARD'
        ? contactPersonFirstName
          ? !validator.isLength(contactPersonFirstName, { min: 3 })
            ? INPUT_ERRORS.NOT_VALID.CONTACT_PERSON_FIRST_NAME_LENGTH
            : !REGEX.USER_NAME.test(contactPersonFirstName)
            ? INPUT_ERRORS.NOT_VALID.CONTACT_PERSON_FIRST_NAME
            : ''
          : ''
        : '',
    contactPersonLastName:
      belongsTo !== 'YARD'
        ? contactPersonLastName
          ? !validator.isLength(contactPersonLastName, { min: 3 })
            ? INPUT_ERRORS.NOT_VALID.CONTACT_PERSON_LAST_NAME_LENGTH
            : !REGEX.USER_NAME.test(contactPersonLastName)
            ? INPUT_ERRORS.NOT_VALID.CONTACT_PERSON_LAST_NAME
            : ''
          : ''
        : '',
    email:
      belongsTo !== 'YARD'
        ? email
          ? !validator.isLength(email, { min: 6, max: 50 })
            ? INPUT_ERRORS.NOT_VALID.EMAIL_LENGTH
            : !REGEX.EMAIL.test(email)
            ? INPUT_ERRORS.NOT_VALID.EMAIL
            : ''
          : ''
        : '',
    phoneNumber: phoneNumber
      ? !isValidPhoneNumber(`+${getPhoneNumberValue(phoneNumber)}`)
        ? belongsTo !== 'YARD'
          ? INPUT_ERRORS.NOT_VALID.CONTACT_PERSON_PHONE_NUMBER
          : INPUT_ERRORS.NOT_VALID.PHONE_NUMBER
        : ''
      : '',
    name:
      belongsTo === 'YARD'
        ? name
          ? !validator.isLength(name, { min: 6, max: 50 })
            ? INPUT_ERRORS.NOT_VALID.YARD_NAME_LENGTH
            : !REGEX.COMPANY_NAME.test(name)
            ? INPUT_ERRORS.NOT_VALID.YARD_NAME
            : ''
          : true
        : '',
    companyEmail:
      belongsTo !== 'YARD'
        ? companyEmail
          ? !validator.isLength(companyEmail, { min: 6, max: 50 })
            ? INPUT_ERRORS.NOT_VALID.COMPANY_EMAIL_LENGTH
            : !REGEX.EMAIL.test(companyEmail)
            ? INPUT_ERRORS.NOT_VALID.COMPANY_EMAIL
            : ''
          : true
        : '',
    companyPhoneNumber:
      belongsTo !== 'YARD'
        ? companyPhoneNumber
          ? !isValidPhoneNumber(`+${getPhoneNumberValue(companyPhoneNumber)}`)
            ? INPUT_ERRORS.NOT_VALID.COMPANY_PHONE_NUMBER
            : ''
          : true
        : '',
    yardCompanyId: belongsTo === 'YARD' ? (!yardCompanyId ? true : '') : '',
    yardId:
      belongsTo === 'CLIENT' && showToRoles(['YARD_COMPANY_ADMIN', 'YARD_COMPANY_EMPLOYEE'], user)
        ? !yardId
        : '',
    blocks: blocks.map((item) => ({
      block: item.block
        ? !validator.isLength(item.block, { max: 30 })
          ? INPUT_ERRORS.NOT_VALID.BLOCK_NAME_LENGTH
          : ''
        : '',
    })),
    messengers: messengers.map((item) => ({
      label: item.label
        ? !validator.isLength(item.label, { max: 30 })
          ? INPUT_ERRORS.NOT_VALID.MESSENGER_NAME_LENGTH
          : ''
        : '',
      identifier: item.identifier
        ? !validator.isLength(item.identifier, { max: 100 })
          ? INPUT_ERRORS.NOT_VALID.MESSENGER_IDENTIFIER_LENGTH
          : ''
        : '',
    })),
    descriptions: descriptions.map((val: string) =>
      val
        ? !validator.isLength(val, { max: 100 })
          ? INPUT_ERRORS.NOT_VALID.FILE_NAME_LENGTH
          : ''
        : '',
    ),
  };

  useEffect(() => {
    const notificationData = getNotificationData();
    if (notificationData && belongsTo === 'CLIENT') {
      setCompanyLegalName(notificationData.legalName);
      setCode(notificationData.legalName && getCompanyCode(notificationData.legalName));
      setCountry(notificationData.address?.country);
      setCity(notificationData.address?.city);
      setStreetAddress(notificationData.address?.streetAddress);
      setContactPersonFirstName(notificationData.contactPerson?.firstName);
      setContactPersonLastName(notificationData.contactPerson?.lastName);
      setCompanyEmail(notificationData.email);
      setCompanyPhoneNumber(notificationData.phoneNumber);
      setMessengers(notificationData.messengers);
    }
    if (!formProps) return;

    formProps = { isSubmitClicked: false };
    setUpdate(Math.random());
  }, []);

  useEffect(() => {
    formProps = {
      ...formProps,
      companyLegalName,
      streetAddress,
      city,
      zipCode,
      country,
      contactPersonFirstName,
      contactPersonLastName,
      email,
      phoneNumber,
      name,
      companyEmail,
      companyPhoneNumber,
      yardCompanyId,
      yardId,
      category,
      gateOutAllowed,
      blocks,
      messengers,
      files,
      descriptions,
      validators,
      onSubmitClick: () => {
        formProps.isSubmitClicked = true;
        setUpdate(Math.random());
      },
      resolveFn: () => {
        setCompanyLegalName('');
        if (belongsTo === 'CLIENT' || belongsTo === 'YARD') setCode('');
        setStreetAddress('');
        setCity('');
        setZipCode('');
        setCountry('');
        setContactPersonFirstName('');
        setContactPersonLastName('');
        setEmail('');
        setPhoneNumber('');
        setName('');
        setCompanyEmail('');
        setCompanyPhoneNumber('');
        setYardCompanyId('');
        setYardCompanyName('');
        setYardId('');
        setCategory('NONE');
        setGateOutAllowed(false);
        setBlocks([{ block: '', rowLength: '', slotLength: '' }]);
        setMessengers([{ label: '', identifier: '' }]);
        setFiles([]);
        setDescriptions([]);
        setUpdate(Math.random());
      },
    };
    if (belongsTo === 'CLIENT' || belongsTo === 'YARD') formProps.code = code;
  }, [
    companyLegalName,
    code,
    streetAddress,
    city,
    zipCode,
    country,
    contactPersonFirstName,
    contactPersonLastName,
    email,
    phoneNumber,
    name,
    companyEmail,
    companyPhoneNumber,
    yardCompanyId,
    yardId,
    category,
    gateOutAllowed,
    JSON.stringify(blocks),
    JSON.stringify(messengers),
    descriptions.join(''),
    files.length,
  ]);

  const handleSelectCountry = (country: any) => {
    setCity('');
    setCountry(country);
  };

  const handleAddBlockClick = () => {
    setBlocks([...blocks, { block: '', rowLength: '', slotLength: '' }]);
  };

  const handleDeleteBlockClick = (i: number) => {
    setBlocks(blocks.filter((_: any, index: number) => index !== i));

    setUpdate(Math.random());
  };

  const handleAddMessengerClick = () => {
    setMessengers([...messengers, { label: '', identifier: '' }]);
  };

  const handleDeleteClick = (i: number) => {
    setMessengers(messengers.filter((_: any, index: number) => index !== i));

    setUpdate(Math.random());
  };

  const handleChangeFiles = (_: any, newFiles: any) => {
    setFiles([...files, ...newFiles]);
    setDescriptions([...descriptions, ...newFiles.fill('')]);
    inputRef.current.type = 'text';
    inputRef.current.type = 'file';
  };

  const handleDeleteFileClick = (i: number) => {
    setDescriptions(descriptions.filter((_: any, index: number) => index !== i));
    setFiles(files.filter((_: any, index: number) => index !== i));
    setUpdate(Math.random());
  };

  return (
    <DashboardForm key={updateKey} paperSize={'full-size'}>
      {belongsTo === 'CLIENT' &&
        showToRoles(['YARD_COMPANY_ADMIN', 'YARD_COMPANY_EMPLOYEE'], user) && (
          <BelongsToSelects
            hidePlaces
            handleYardChange={(obj) => setYardId(obj.id)}
            isSubmitClicked={formProps.isSubmitClicked}
          />
        )}
      {belongsTo === 'YARD' && (
        <DashboardFormTwoColumn>
          <SearchableSelect
            selectedOptionLabel={yardCompanyName}
            label={i18n.t('common.yardCompany') + '*'}
            onSelect={(obj: any) => {
              setYardCompanyName(obj.label);
              setYardCompanyId(obj.id);
            }}
            queryFn={(inputValue: any) => belongsToOptions.YardCompany(inputValue, true)}
            queryKey={['yard-companies-short', 'without-all']}
            error={
              (formProps.isSubmitClicked && !yardCompanyId) ||
              (!!yardCompanyId && !!validators.yardCompanyId)
            }
            helperText={
              formProps.isSubmitClicked && !yardCompanyId ? INPUT_ERRORS.REQUIRED.YARD_COMPANY : ''
            }
            selectedAreasEqaul={{ key: 'id', keys: [yardCompanyId] }}
          />
          <Input
            defaultValue={name}
            onChange={(e: any) => setName(e.target.value)}
            label={i18n.t('label.yardName') + '*'}
            type={'text'}
            data-size={'medium'}
            error={(formProps.isSubmitClicked && !name) || (!!name && validators.name)}
            helperText={
              formProps.isSubmitClicked && !name ? INPUT_ERRORS.REQUIRED.YARD_NAME : validators.name
            }
          />
        </DashboardFormTwoColumn>
      )}
      {belongsTo === 'YARD_COMPANIES' && (
        <Input
          defaultValue={companyLegalName}
          onChange={(e: any) => setCompanyLegalName(e.target.value)}
          label={
            belongsTo !== 'YARD'
              ? i18n.t('label.companyLegalName') + '*'
              : i18n.t('label.yardName') + '*'
          }
          type={'text'}
          data-size={'medium'}
          error={
            (formProps.isSubmitClicked && !companyLegalName) ||
            (!!companyLegalName && validators.companyLegalName)
          }
          helperText={
            formProps.isSubmitClicked && !companyLegalName
              ? INPUT_ERRORS.REQUIRED.COMPANY_LEGAL_NAME
              : validators.companyLegalName
          }
        />
      )}
      <DashboardFormTwoColumn>
        {belongsTo === 'CLIENT' && (
          <Input
            defaultValue={companyLegalName}
            onChange={(e: any) => {
              setCode(getCompanyCode(e.target.value));
              setCompanyLegalName(e.target.value);
            }}
            label={
              belongsTo !== 'YARD'
                ? i18n.t('label.companyLegalName') + '*'
                : i18n.t('label.yardName') + '*'
            }
            type={'text'}
            data-size={'medium'}
            error={
              (formProps.isSubmitClicked && !companyLegalName) ||
              (!!companyLegalName && validators.companyLegalName)
            }
            helperText={
              formProps.isSubmitClicked && !companyLegalName
                ? INPUT_ERRORS.REQUIRED.COMPANY_LEGAL_NAME
                : validators.companyLegalName
            }
          />
        )}
        {belongsTo === 'CLIENT' && (
          <Input
            value={code}
            onChange={(e: any) => setCode(e.target.value)}
            label={i18n.t('label.code') + '*'}
            type={'text'}
            data-size={'medium'}
            error={(formProps.isSubmitClicked && !code) || (!!code && validators.code)}
            helperText={
              formProps.isSubmitClicked && !code ? INPUT_ERRORS.REQUIRED.CODE : validators.code
            }
            disabled={!showToRoles(['SUPER_ADMIN'], user)}
          />
        )}
        {belongsTo !== 'YARD' && (
          <>
            <SearchableSelect
              id={i18n.t('label.country') + '*'}
              selectedOptionLabel={country}
              label={i18n.t('label.country') + '*'}
              options={CountryOptions}
              onChange={(e: any) => {
                setCity('');
                setCountry(e.target.value);
              }}
              error={(formProps.isSubmitClicked && !country) || (!!country && !!validators.country)}
              helperText={
                formProps.isSubmitClicked && !country
                  ? INPUT_ERRORS.REQUIRED.COUNTRY
                  : validators.country
              }
            />
            <SearchableSelect
              id={i18n.t('label.city') + '*'}
              selectedOptionLabel={city}
              label={i18n.t('label.city') + '*'}
              options={StateOptions(country)}
              onChange={(e: any) => setCity(e.target.value)}
              error={(formProps.isSubmitClicked && !city) || (!!city && !!validators.city)}
              helperText={
                formProps.isSubmitClicked && !city ? INPUT_ERRORS.REQUIRED.CITY : validators.city
              }
              disabled={!country}
            />
          </>
        )}
        {belongsTo === 'YARD' && (
          <>
            <SearchableSelect
              selectedOptionLabel={country}
              label={i18n.t('label.country') + '*'}
              onSelect={handleSelectCountry}
              options={countries}
              error={(formProps.isSubmitClicked && !country) || (!!country && !!validators.country)}
              helperText={
                formProps.isSubmitClicked && !country
                  ? INPUT_ERRORS.REQUIRED.COUNTRY
                  : typeof validators.country === 'string'
                  ? validators.country
                  : ''
              }
            />
            <SearchableSelect
              selectedOptionLabel={city}
              label={i18n.t('label.city') + '*'}
              onSelect={setCity}
              options={cities[country]}
              disabled={!country}
              error={(formProps.isSubmitClicked && !city) || (!!city && !!validators.city)}
              helperText={
                formProps.isSubmitClicked && !city
                  ? INPUT_ERRORS.REQUIRED.CITY
                  : typeof validators.city === 'string'
                  ? validators.city
                  : ''
              }
            />
          </>
        )}
        <Input
          defaultValue={streetAddress}
          onChange={(e: any) => setStreetAddress(e.target.value)}
          label={i18n.t('label.streetAddress') + '*'}
          type={'text'}
          data-size={'medium'}
          error={
            (formProps.isSubmitClicked && !streetAddress) ||
            (!!streetAddress && validators.streetAddress)
          }
          helperText={
            formProps.isSubmitClicked && !streetAddress
              ? INPUT_ERRORS.REQUIRED.STREET_ADDRESS
              : validators.streetAddress
          }
        />
        <Input
          defaultValue={zipCode}
          onChange={(e: any) => setZipCode(e.target.value)}
          label={i18n.t('label.zipCode')}
          type={'text'}
          data-size={'medium'}
          error={!!zipCode && !!validators.zipCode}
          helperText={validators.zipCode}
        />
        {belongsTo !== 'YARD' && (
          <Input
            defaultValue={companyEmail}
            onChange={(e: any) => setCompanyEmail(e.target.value)}
            label={i18n.t('label.companyEmail') + '*'}
            type={'text'}
            data-size={'medium'}
            withoutSpace={true}
            error={
              (formProps.isSubmitClicked && !companyEmail) ||
              (!!companyEmail && validators.companyEmail)
            }
            helperText={
              formProps.isSubmitClicked && !companyEmail
                ? INPUT_ERRORS.REQUIRED.COMPANY_EMAIL
                : validators.companyEmail
            }
          />
        )}
        {belongsTo !== 'YARD' && (
          <InputPhoneNumber
            value={companyPhoneNumber}
            onChange={(e: any) => setCompanyPhoneNumber(e.target.value)}
            label={i18n.t('label.companyPhoneNumber') + '*'}
            data-size={'medium'}
            error={
              (formProps.isSubmitClicked && !companyPhoneNumber) ||
              (!!companyPhoneNumber && validators.companyPhoneNumber)
            }
            helperText={
              formProps.isSubmitClicked && !companyPhoneNumber
                ? INPUT_ERRORS.REQUIRED.COMPANY_PHONE_NUMBER
                : validators.companyPhoneNumber
            }
          />
        )}
        {belongsTo !== 'YARD' && <BorderLine space={'dashboard-form'} gridColumn={'1 / 3'} />}
        {belongsTo !== 'YARD' && (
          <Input
            defaultValue={contactPersonFirstName}
            onChange={(e: any) => setContactPersonFirstName(e.target.value)}
            label={i18n.t('label.contactPersonFirstName')}
            type={'text'}
            data-size={'medium'}
            error={!!contactPersonFirstName && !!validators.contactPersonFirstName}
            helperText={validators.contactPersonFirstName}
          />
        )}
        {belongsTo !== 'YARD' && (
          <Input
            defaultValue={contactPersonLastName}
            onChange={(e: any) => setContactPersonLastName(e.target.value)}
            label={i18n.t('label.contactPersonLastName')}
            type={'text'}
            data-size={'medium'}
            error={!!contactPersonLastName && !!validators.contactPersonLastName}
            helperText={validators.contactPersonLastName}
          />
        )}
        {belongsTo !== 'YARD' && (
          <Input
            defaultValue={email}
            onChange={(e: any) => setEmail(e.target.value)}
            label={i18n.t('label.contactPersonEmail')}
            type={'text'}
            data-size={'medium'}
            withoutSpace={true}
            error={!!email && !!validators.email}
            helperText={validators.email}
          />
        )}
        <InputPhoneNumber
          value={phoneNumber}
          onChange={(e: any) => setPhoneNumber(e.target.value)}
          label={`${belongsTo !== 'YARD' ? `${i18n.t('label.contactPerson')} ` : ''}${i18n.t(
            'label.phoneNumber',
          )}`}
          data-size={'medium'}
          error={!!phoneNumber && !!validators.phoneNumber}
          helperText={validators.phoneNumber}
        />
        {belongsTo === 'YARD' && (
          <Input
            defaultValue={code}
            onChange={(e: any) => setCode(e.target.value)}
            label={i18n.t('label.code') + '*'}
            type={'text'}
            data-size={'medium'}
            error={(formProps.isSubmitClicked && !code) || (!!code && validators.code)}
            helperText={
              formProps.isSubmitClicked && !code ? INPUT_ERRORS.REQUIRED.CODE : validators.code
            }
          />
        )}
        {belongsTo === 'CLIENT' && (
          <>
            <Select
              label={i18n.t('label.category')}
              options={clientCategories}
              value={category}
              onChange={(e) => setCategory(e.target.value)}
            />
            <Select
              label={i18n.t('label.gateOutAuthorization')}
              options={gateOutAllowedOptions}
              value={gateOutAllowed}
              onChange={(e) => setGateOutAllowed(e.target.value)}
            />
          </>
        )}
      </DashboardFormTwoColumn>
      {belongsTo === 'YARD' && (
        <>
          <BorderLine space={'dashboard-form'} />
          {blocks.map((item, i) => (
            <DashboardFormCustomColumn
              key={updateKey + i}
              columns={'calc(100%/3 - 2.8rem*(2/3)) calc(100%/3 - 2.8rem*(2/3)) 1fr max-content'}
              align={'start'}
            >
              <Input
                id={i18n.t('label.block') + i}
                label={i18n.t('label.block')}
                defaultValue={item.block}
                onChange={(e: any) => {
                  const newState = blocks;
                  newState[i].block = e.target.value;
                  setBlocks([...newState]);
                }}
                type={'text'}
                data-size={'medium'}
                error={formProps.isSubmitClicked && !!validators.blocks[i].block}
                helperText={formProps.isSubmitClicked && validators.blocks[i].block}
              />
              <InputNumber
                id={i18n.t('label.rowLength') + i}
                label={i18n.t('label.rowLength')}
                defaultValue={item.rowLength}
                minValue={1}
                maxValue={40}
                onChange={(e: any) => {
                  const newState = blocks;
                  newState[i].rowLength = e.target.value;
                  setBlocks([...newState]);
                }}
                data-size={'medium'}
              />
              <InputNumber
                id={i18n.t('label.slotLength') + i}
                label={i18n.t('label.slotLength')}
                defaultValue={item.slotLength}
                minValue={1}
                maxValue={40}
                onChange={(e: any) => {
                  const newState = blocks;
                  newState[i].slotLength = e.target.value;
                  setBlocks([...newState]);
                }}
                data-size={'medium'}
              />
              {i === blocks.length - 1 ? (
                <Button
                  className={'row-button-square'}
                  size={'medium'}
                  data-color={'secondary'}
                  isSquare={true}
                  onClick={handleAddBlockClick}
                >
                  <AddIcon />
                </Button>
              ) : (
                <Button
                  className={'row-button-square'}
                  size={'medium'}
                  data-color={'destructiveSecondary'}
                  isSquare={true}
                  onClick={() => handleDeleteBlockClick(i)}
                >
                  <DeleteIcon />
                </Button>
              )}
            </DashboardFormCustomColumn>
          ))}
        </>
      )}
      <BorderLine space={'dashboard-form'} />
      {messengers.map((_: any, i: number) => (
        <DashboardFormCustomColumn
          key={updateKey + i}
          columns={'calc(100%/2 - 2.8rem*(1/2)) 1fr max-content'}
          align={'start'}
        >
          <SearchableSelect
            id={i18n.t('label.messengerName') + i}
            selectedOptionLabel={messengers[i].label}
            label={i18n.t('label.messengerName')}
            queryFn={(inputValue) => {
              const isNewOption = !messengerOptions.filter((item) =>
                item.label.toLowerCase().includes(inputValue.toLowerCase()),
              ).length;
              if (isNewOption) {
                return () => [{ label: inputValue, value: inputValue }];
              }

              return () => messengerOptions;
            }}
            queryKey={'messengers'}
            selectedAreasEqaul={{ key: 'value', keys: [messengers[i].label] }}
            onChange={(e: any) => {
              const newState = messengers;
              newState[i].label = e.target.value;
              setMessengers([...newState]);
            }}
            error={formProps.isSubmitClicked && !!validators.messengers[i].label}
            helperText={formProps.isSubmitClicked && validators.messengers[i].label}
          />
          <Input
            id={i18n.t('label.messengerIdentifier') + i}
            label={i18n.t('label.messengerIdentifier')}
            defaultValue={messengers[i].identifier}
            onChange={(e: any) => {
              const newState = messengers;
              newState[i].identifier = e.target.value;
              setMessengers([...newState]);
            }}
            type={'text'}
            data-size={'medium'}
            error={formProps.isSubmitClicked && !!validators.messengers[i].identifier}
            helperText={formProps.isSubmitClicked && validators.messengers[i].identifier}
          />
          {i === messengers.length - 1 ? (
            <Button
              className={'row-button-square'}
              size={'medium'}
              data-color={'secondary'}
              isSquare={true}
              onClick={handleAddMessengerClick}
            >
              <AddIcon />
            </Button>
          ) : (
            <Button
              className={'row-button-square'}
              size={'medium'}
              data-color={'destructiveSecondary'}
              isSquare={true}
              onClick={() => handleDeleteClick(i)}
            >
              <DeleteIcon />
            </Button>
          )}
        </DashboardFormCustomColumn>
      ))}
      {belongsTo === 'CLIENT' && (
        <>
          <BorderLine space={'dashboard-form'} />
          {files.map((file: any, i: any) => (
            <DashboardFormCustomColumn
              key={updateKey + 'files' + i}
              columns={'1fr 1fr'}
              align={'start'}
            >
              <Input
                id={i18n.t('label.fileName') + i}
                label={i18n.t('label.fileName')}
                defaultValue={descriptions[i]}
                onChange={(e: any) => {
                  const newState = descriptions;
                  newState[i] = e.target.value;
                  setDescriptions([...newState]);
                }}
                type={'text'}
                data-size={'medium'}
                error={formProps.isSubmitClicked && !!validators.descriptions[i]}
                helperText={formProps.isSubmitClicked && validators.descriptions[i]}
              />
              <FileRow
                key={i}
                file={file}
                handleDeleteFileClick={() => handleDeleteFileClick(i)}
                variant={'input'}
                withoutBorder
              />
            </DashboardFormCustomColumn>
          ))}
          <HideInput>
            <FileInput
              id={'file-input'}
              label={i18n.t('dropdown.addImages')}
              inputRef={inputRef}
              inputProps={{
                accept: '*',
                multiple: true,
              }}
              handleChangeFiles={handleChangeFiles}
              withoutCompress
            />
          </HideInput>
          <Button size={'medium'} justify={'left'} onClick={() => inputRef.current.click()}>
            {i18n.t('button.addDocuments')}
          </Button>
        </>
      )}
    </DashboardForm>
  );
};
